import React, { useState } from 'react'
import moment from 'moment'
import { Modal, ModalBody, ModalHeader, Table } from 'reactstrap'
import { FaExclamationTriangle } from 'react-icons/fa'
import {
  TRIVIA_USER_IDENTIFICATION_OPTIONS,
  SENTIMENTS_TYPO_MAP,
} from '../data'
import { profilePicValidityCheck } from '../../../utils/constants'
import MessagingResponseFullModal from './Results/MessagingResponseFullModal'
import '../trivvy-response-modal-bwai.css'

// Sibling to NeedsAttentionModal — same identity-rendering rules, but the
// messaging flow diverges on three axes:
//   1. Different columns (Sentiment + truncated Response excerpt; no
//      Total Duration).
//   2. Different field-name semantics (sent vs. response dates are
//      inverted vs. SURVEY — see field-mapping note below).
//   3. Row click renders TriviaSMSResponses (chat panel autoload),
//      not MyTriviaDetails. We reuse MessagingResponseFullModal for that.
//
// NOTE on the anonymity gate: the SURVEY NeedsAttentionModal intentionally
// overrides the `allowAnonymousResponses && responsesData.length <= 5`
// cloak from the By User tab. Messaging today has no global "<=5" cloak,
// only per-row `result.isAnonymous`, so there's nothing to override —
// we just respect the per-row flag.

const getInitials = (user) => {
  const firstName =
    user && user.firstName && user.firstName.length > 0 ? user.firstName : 'N'
  const lastName =
    user && user.lastName && user.lastName.length > 0 ? user.lastName : 'N'

  if (firstName === 'N' && lastName === 'N') {
    if (user && user.email) {
      return user.email.charAt(0) + user.email.split('@')[1].charAt(0)
    }
    return ''
  }
  return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()
}

// Keep in sync with the helper of the same name in NeedsAttentionModal.jsx
// and TriviaResultsResponses.jsx (private inner helper there).
const constructUserObjForExternalUser = (obj) => {
  if (!obj) return null
  if (obj.email) {
    return {
      firstName: obj.email,
      identification: '(email)',
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.name) {
    const arr = obj.name.split(' ')
    return {
      identification: '(name)',
      firstName: arr[0],
      lastName: arr[1] || '',
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.mobileNumber) {
    return {
      identification: '(phone)',
      firstName: obj.mobileNumber,
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.firstName || obj.lastName) {
    return {
      identification: '(name)',
      firstName: obj.firstName || '',
      lastName: obj.lastName || '',
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.employeeId) {
    return {
      identification: '(employeeId)',
      firstName: obj.employeeId,
      lastName: obj.lastName || '',
      email: '',
      mobileNumber: '',
      employeeId: obj.employeeId,
    }
  }
  return {
    identification: 'anonymous',
    firstName: obj.firstName || '',
    lastName: obj.lastName || '',
    email: obj.email,
    mobileNumber: obj.mobileNumber,
    employeeId: obj.employeeId,
  }
}

const getIdentityDisplay = (user, isAnonymousFlag, currentTrivvy) => {
  if (!user || isAnonymousFlag) {
    return 'Anonymous user'
  }

  let pref = ''
  if (currentTrivvy && currentTrivvy.userIdentificationOptions) {
    pref = Array.isArray(currentTrivvy.userIdentificationOptions)
      ? currentTrivvy.userIdentificationOptions[0]
      : currentTrivvy.userIdentificationOptions
  }

  let field = ''
  let val = ''
  for (let i = 0; i < TRIVIA_USER_IDENTIFICATION_OPTIONS.length; i++) {
    const opt = TRIVIA_USER_IDENTIFICATION_OPTIONS[i]
    if (opt.value === pref) {
      field = opt.field || 'email'
      val = field ? user[field] : ''
      if (opt.value === 'FIRST_LAST_NAME') {
        val =
          (user.firstName ? user.firstName : '') +
          ' ' +
          (user.lastName ? user.lastName : '')
      }
      break
    }
  }
  val = val != null ? String(val).trim() : ''
  if (!val) {
    return 'Anonymous user'
  }
  return val
}

const hasIdentityValue = (obj) => {
  if (!obj || typeof obj !== 'object') return false
  const keys = ['email', 'employeeId', 'mobileNumber', 'firstName', 'lastName']
  for (let i = 0; i < keys.length; i++) {
    const v = obj[keys[i]]
    if (v !== null && v !== undefined && String(v).trim() !== '') {
      return true
    }
  }
  return false
}

const MessagingNeedsAttentionModal = ({ open, onClose, responses, currentTrivvy, user }) => {
  const [viewerData, setViewerData] = useState(null)

  const flagged = Array.isArray(responses)
    ? responses.filter((r) => r && r.needsAttention === true)
    : []

  // Row mapping mirrors MessagingTriviaResultsResponses.jsx (the legacy
  // table). Field-name inversion is intentional and matches that file —
  // do NOT swap:
  //   date       = messagingResponse.respondedDate || createdOn  -> "Response Date"
  //   sentDate   = dateTrivvySent || isSubmittedDate             -> "Sent Date"
  const rows = flagged.map((res) => {
    const rawUser = res.userId
      ? res.userId
      : res.userData
        ? constructUserObjForExternalUser(res.userData)
        : null
    return {
      _id: res._id,
      user: rawUser,
      isAnonymous: res.isAnonymous,
      response: res.messagingResponse
        ? res.messagingResponse.text
        : 'No response',
      // Mirror the Submissions modal: render the canonical response
      // timestamp (`isSubmittedDate`) under the "Response Date" column.
      // The legacy `dateTrivvySent`-first mapping was effectively a SENT
      // date, which is not what the CEO's spec calls for here.
      date: res.isSubmittedDate,
      sentDate: res.isSubmittedDate || '',
      sentimentData:
        res.sentimentsScore && res.sentimentsScore.sentiment
          ? res.sentimentsScore
          : null,
      needsAttention: res.needsAttention,
      _raw: res,
    }
  })

  const handleClose = () => {
    // Clear the inner viewer state when the outer modal closes so it can't
    // outlive its parent. Mirrors the SURVEY pattern in
    // TriviaResultsInsights.jsx (parent-controlled clear).
    setViewerData(null)
    onClose()
  }

  const handleRowClick = (row) => {
    if (!row || !row._raw) return
    const res = row._raw
    // Stamp comingFromNeedsAttention so TriviaSMSResponses auto-opens the
    // chat panel and fetches the conversation on mount.
    const payload = {
      ...res,
      user: row.user,
      response: row.response,
      date: row.date,
      sentDate: row.sentDate,
      sentimentData: row.sentimentData,
      isAnonymous: row.isAnonymous,
      needsAttention: row.needsAttention,
      comingFromNeedsAttention: true,
    }
    setViewerData(payload)
  }

  const renderIdentityCell = (row) => {
    const showRealIdentity = hasIdentityValue(row.user) && !row.isAnonymous
    if (showRealIdentity) {
      return (
        <span className="trivia_table_text_wrapper">
          {profilePicValidityCheck(row.user.profilePic) ? (
            <img
              src={row.user.profilePic}
              height="35px"
              width="35px"
              className="rounded-circle"
              alt=""
            />
          ) : (
            <span
              className="spotlightUserInitials"
              data-letters={getInitials(row.user)}
            ></span>
          )}{' '}
          {(row.user.firstName ? row.user.firstName : '') +
            ' ' +
            (row.user.lastName ? row.user.lastName : '') +
            ' ' +
            ((row.user.firstName === '' && row.user.lastName === '') ||
              (!row.user.firstName && !row.user.lastName)
              ? getIdentityDisplay(row.user, row.isAnonymous, currentTrivvy)
              : '')}
        </span>
      )
    }
    return (
      <span className="trivia_table_text_wrapper">
        <span className="spotlightUserInitials" data-letters={'AU'}></span>
        Anonymous user
      </span>
    )
  }

  return (
    <>
      <Modal isOpen={open} toggle={handleClose} size="lg" className="bwai-response-modal">
        <ModalHeader className="trivia_modal_header_bg" toggle={handleClose}>
          Responses that need attention
        </ModalHeader>
        <ModalBody>
          <div className="mb-3 bwai-needs-attention-muted">
            The list below shows messaging responses flagged as needing
            attention. Click a row to review the full response and conversation.
          </div>

          {rows.length === 0 ? (
            <div className="text-center py-4 bwai-needs-attention-muted">
              No responses currently need attention.
            </div>
          ) : (
            <div className="bwai-needs-attention-scroll">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    {/* <th>User</th> */}
                    <th className="text-center">Response Date</th>
                    <th className="text-center">Sentiment</th>
                    <th className="text-center">Needs Attention</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row._id}
                      className="cursor-pointer"
                      onClick={() => handleRowClick(row)}
                    >
                      {/* <td>{renderIdentityCell(row)}</td> */}
                      <td className="text-center">
                        <span className="trivia_table_text_wrapper">
                          {row.date ? (
                            <>
                              {moment(row.date).format('ll')}
                              <br />
                              {moment(row.date).format('LT')}
                            </>
                          ) : (
                            '—'
                          )}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="trivia_table_text_wrapper">
                          {row.sentimentData && row.sentimentData.sentiment
                            ? SENTIMENTS_TYPO_MAP[row.sentimentData.sentiment] || '—'
                            : '—'}
                        </span>
                      </td>
                      <td className="text-center">
                        {row.needsAttention ? (
                          <FaExclamationTriangle color="goldenrod" size="1.8em" />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>

      {viewerData && (
        <MessagingResponseFullModal
          data={viewerData}
          onClose={() => setViewerData(null)}
          popoverIdSuffix="needs_attention"
          user={user}
          adminAId={
            currentTrivvy &&
            currentTrivvy.createdBy &&
            (currentTrivvy.createdBy._id || currentTrivvy.createdBy)
          }
        />
      )}
    </>
  )
}

export default MessagingNeedsAttentionModal
